import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

export const fetchDeviceConfig = createAsyncThunk(
  "deviceConfig/fetch",
  async (imei, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/configuration/imei/${encodeURIComponent(imei)}`
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load configuration"));
    }
  }
);

export const updateDeviceConfig = createAsyncThunk(
  "deviceConfig/update",
  async ({ imei, data }, { rejectWithValue }) => {
    try {
      // The c-grid values may be ALL CAPS via the global axios interceptor.
      // We pass `skipUppercase: true` so the user-typed casing for field
      // names / values is preserved exactly as entered.
      const res = await api.put(
        `/configuration/imei/${encodeURIComponent(imei)}`,
        { data },
        { skipUppercase: true }
      );
      return res.data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to save configuration"));
    }
  }
);

const initialState = {
  imei: null,
  data: null, // { c1: {name, value}, ..., c40: {name, value} }
  version: null,
  updatedAt: null,
  loading: false,
  saving: false,
  error: null,
};

const slice = createSlice({
  name: "deviceConfig",
  initialState,
  reducers: {
    clearDeviceConfig() {
      return initialState;
    },
    setLocalField(state, { payload }) {
      const { key, field, value } = payload;
      if (!state.data || !state.data[key]) return;
      state.data[key] = { ...state.data[key], [field]: value };
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchDeviceConfig.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchDeviceConfig.fulfilled, (s, a) => {
        s.loading = false;
        s.imei = a.payload.imei;
        s.data = a.payload.data;
        s.updatedAt = a.payload.updatedAt;
        s.version = a.payload.data?.c40?.value ?? null;
      })
      .addCase(fetchDeviceConfig.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(updateDeviceConfig.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(updateDeviceConfig.fulfilled, (s, a) => {
        s.saving = false;
        const fresh = a.payload?.data;
        if (fresh?.data) {
          s.data = fresh.data;
          s.version = fresh.data.c40?.value ?? a.payload.version ?? s.version;
          s.updatedAt = fresh.updatedAt || s.updatedAt;
        } else if (a.payload?.version != null) {
          s.version = String(a.payload.version);
        }
      })
      .addCase(updateDeviceConfig.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      });
  },
});

export const { clearDeviceConfig, setLocalField } = slice.actions;
export default slice.reducer;
